import { useEffect, useCallback, useRef } from 'react';

interface ScrollConfig {
    navbarHeight: number;
    anchorMenuHeight: number;
    extraOffset: number;
    minDuration: number;
    maxDuration: number;
    scrollSpeed: number;
}

const DEFAULT_CONFIG: ScrollConfig = {
    navbarHeight: 64,
    anchorMenuHeight: 50,
    extraOffset: -40,
    minDuration: 2000,
    maxDuration: 2000,
    scrollSpeed: 1.0,
};

const globalPositionCache = new Map<string, { position: number; timestamp: number }>();
const CACHE_DURATION = 2000;

let isScrollingGlobal = false;

const clearExpiredCache = () => {
    const now = Date.now();
    //@ts-ignore
    for (const [key, value] of globalPositionCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
            globalPositionCache.delete(key);
        }
    }
};

const calculateTargetPosition = (elementId: string, config: ScrollConfig): number => {
    const cacheKey = `${elementId}_${config.navbarHeight}_${config.anchorMenuHeight}_${config.extraOffset}`;
    const cached = globalPositionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.position;
    }

    const element = document.getElementById(elementId);
    if (!element) return 0;

    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + window.scrollY;
    const totalOffset = config.navbarHeight + config.anchorMenuHeight + config.extraOffset;
    const targetPosition = Math.max(0, elementTop - totalOffset);

    globalPositionCache.set(cacheKey, {
        position: targetPosition,
        timestamp: Date.now()
    });

    return targetPosition;
};

const calculateScrollDuration = (distance: number, config: ScrollConfig): number => {
    const duration = Math.abs(distance) / config.scrollSpeed;
    return Math.max(config.minDuration, Math.min(config.maxDuration, duration));
};

const performInstantScroll = (targetY: number, duration: number): void => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    
    if (Math.abs(distance) < 10) {
        window.scrollTo(0, targetY);
        isScrollingGlobal = false;
        return;
    }
    let t0 = performance.now();
    let t1 = performance.now();
    console.log(t1 - t0);

    const startTime = performance.now();

    const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const scrollStep = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        window.scrollTo(0, startY + distance * easedProgress);

        if (progress < 1) {
            requestAnimationFrame(scrollStep);
        } else {
            isScrollingGlobal = false;
        }
    };

    requestAnimationFrame(scrollStep);
};

export const useInstantScroll = (config: Partial<ScrollConfig> = {}) => {
    const fullConfig = { ...DEFAULT_CONFIG, ...config };
    const preloadTimeoutRef = useRef<NodeJS.Timeout>();

    const preloadAnchorPositions = useCallback(() => {
        clearExpiredCache();

        const anchors = document.querySelectorAll('a[href*="#"]');
        
        anchors.forEach((anchor) => {
            const href = anchor.getAttribute('href');
            if (href && href.includes('#')) {
                const hash = href.split('#')[1];
                if (hash) {
                    requestIdleCallback(() => {
                        calculateTargetPosition(hash, fullConfig);
                    });
                }
            }
        });
    }, [fullConfig]);

    const scrollToElement = useCallback((elementId: string, updateUrl: boolean = true): boolean => {
        if (isScrollingGlobal) {
            return false;
        }

        let attempts = 0;
        const maxAttempts = 10;
        const interval = 50;

        const tryScroll = () => {
            let element = document.querySelector(`[data-rbd-draggable-id="${elementId}"]`);
            if (!element) {
                element = document.getElementById(elementId);
            }
            if (!element) {
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(tryScroll, interval);
                } else {
                    console.warn(`Element with data-rbd-draggable-id or id "${elementId}" not found after ${maxAttempts} attempts`);
                }
                return;
            }

            isScrollingGlobal = true;

            if (updateUrl) {
                if (window.history.pushState) {
                    window.history.pushState(null, '', `#${elementId}`);
                } else {
                    window.location.hash = elementId;
                }
            }

            if (updateUrl) {
                const oldUrl = window.location.href;
                const base = oldUrl.split('#')[0];
                const newUrl = `${base}#${elementId}`;
                window.history.pushState(null, '', newUrl); 
                window.dispatchEvent(new Event('hashchange'));
              }

            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.scrollY;
            const totalOffset = fullConfig.navbarHeight + fullConfig.anchorMenuHeight + fullConfig.extraOffset;
            const targetPosition = Math.max(0, elementTop - totalOffset);
            const currentPosition = window.scrollY;
            const distance = targetPosition - currentPosition;
            const duration = calculateScrollDuration(distance, fullConfig);
            performInstantScroll(targetPosition, duration);
        };

        tryScroll();
        return true;
    }, [fullConfig]);

    const scrollToHref = useCallback((href: string): boolean => {
        const hash = href.split('#')[1];
        if (!hash) {
            return false;
        }

        return scrollToElement(hash, true);
    }, [scrollToElement]);

    useEffect(() => {
        preloadTimeoutRef.current = setTimeout(() => {
            preloadAnchorPositions();
        }, 100);

        return () => {
            if (preloadTimeoutRef.current) {
                clearTimeout(preloadTimeoutRef.current);
            }
        };
    }, [preloadAnchorPositions]);

    useEffect(() => {
        const handleResize = () => {
            globalPositionCache.clear();
            
            if (preloadTimeoutRef.current) {
                clearTimeout(preloadTimeoutRef.current);
            }
            
            preloadTimeoutRef.current = setTimeout(() => {
                preloadAnchorPositions();
            }, 200);
        };

        window.addEventListener('resize', handleResize, { passive: true });
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [preloadAnchorPositions]);

    return {
        scrollToElement,
        scrollToHref,
        preloadAnchorPositions,
        isScrolling: isScrollingGlobal
    };
};



export default useInstantScroll;import { useEffect, useCallback, useRef } from 'react';

interface ScrollConfig {
    navbarHeight: number;
    anchorMenuHeight: number;
    extraOffset: number;
    minDuration: number;
    maxDuration: number;
    scrollSpeed: number;
}

const DEFAULT_CONFIG: ScrollConfig = {
    navbarHeight: 64,
    anchorMenuHeight: 50,
    extraOffset: -40,
    minDuration: 2000,
    maxDuration: 2000,
    scrollSpeed: 1.0,
};

const globalPositionCache = new Map<string, { position: number; timestamp: number }>();
const CACHE_DURATION = 2000;

let isScrollingGlobal = false;

const clearExpiredCache = () => {
    const now = Date.now();
    //@ts-ignore
    for (const [key, value] of globalPositionCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
            globalPositionCache.delete(key);
        }
    }
};

const calculateTargetPosition = (elementId: string, config: ScrollConfig): number => {
    const cacheKey = `${elementId}_${config.navbarHeight}_${config.anchorMenuHeight}_${config.extraOffset}`;
    const cached = globalPositionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.position;
    }

    const element = document.getElementById(elementId);
    if (!element) return 0;

    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + window.scrollY;
    const totalOffset = config.navbarHeight + config.anchorMenuHeight + config.extraOffset;
    const targetPosition = Math.max(0, elementTop - totalOffset);

    globalPositionCache.set(cacheKey, {
        position: targetPosition,
        timestamp: Date.now()
    });

    return targetPosition;
};

const calculateScrollDuration = (distance: number, config: ScrollConfig): number => {
    const duration = Math.abs(distance) / config.scrollSpeed;
    return Math.max(config.minDuration, Math.min(config.maxDuration, duration));
};

const performInstantScroll = (targetY: number, duration: number): void => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    
    if (Math.abs(distance) < 10) {
        window.scrollTo(0, targetY);
        isScrollingGlobal = false;
        return;
    }
    let t0 = performance.now();
    let t1 = performance.now();
    console.log(t1 - t0);

    const startTime = performance.now();

    const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const scrollStep = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        window.scrollTo(0, startY + distance * easedProgress);

        if (progress < 1) {
            requestAnimationFrame(scrollStep);
        } else {
            isScrollingGlobal = false;
        }
    };

    requestAnimationFrame(scrollStep);
};

export const useInstantScroll = (config: Partial<ScrollConfig> = {}) => {
    const fullConfig = { ...DEFAULT_CONFIG, ...config };
    const preloadTimeoutRef = useRef<NodeJS.Timeout>();

    const preloadAnchorPositions = useCallback(() => {
        clearExpiredCache();

        const anchors = document.querySelectorAll('a[href*="#"]');
        
        anchors.forEach((anchor) => {
            const href = anchor.getAttribute('href');
            if (href && href.includes('#')) {
                const hash = href.split('#')[1];
                if (hash) {
                    requestIdleCallback(() => {
                        calculateTargetPosition(hash, fullConfig);
                    });
                }
            }
        });
    }, [fullConfig]);

    const scrollToElement = useCallback((elementId: string, updateUrl: boolean = true): boolean => {
        if (isScrollingGlobal) {
            return false;
        }

        let attempts = 0;
        const maxAttempts = 10;
        const interval = 50;

        const tryScroll = () => {
            let element = document.querySelector(`[data-rbd-draggable-id="${elementId}"]`);
            if (!element) {
                element = document.getElementById(elementId);
            }
            if (!element) {
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(tryScroll, interval);
                } else {
                    console.warn(`Element with data-rbd-draggable-id or id "${elementId}" not found after ${maxAttempts} attempts`);
                }
                return;
            }

            isScrollingGlobal = true;

            if (updateUrl) {
                if (window.history.pushState) {
                    window.history.pushState(null, '', `#${elementId}`);
                } else {
                    window.location.hash = elementId;
                }
            }

            if (updateUrl) {
                const oldUrl = window.location.href;
                const base = oldUrl.split('#')[0];
                const newUrl = `${base}#${elementId}`;
                window.history.pushState(null, '', newUrl); 
                window.dispatchEvent(new Event('hashchange'));
              }

            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.scrollY;
            const totalOffset = fullConfig.navbarHeight + fullConfig.anchorMenuHeight + fullConfig.extraOffset;
            const targetPosition = Math.max(0, elementTop - totalOffset);
            const currentPosition = window.scrollY;
            const distance = targetPosition - currentPosition;
            const duration = calculateScrollDuration(distance, fullConfig);
            performInstantScroll(targetPosition, duration);
        };

        tryScroll();
        return true;
    }, [fullConfig]);

    const scrollToHref = useCallback((href: string): boolean => {
        const hash = href.split('#')[1];
        if (!hash) {
            return false;
        }

        return scrollToElement(hash, true);
    }, [scrollToElement]);

    useEffect(() => {
        preloadTimeoutRef.current = setTimeout(() => {
            preloadAnchorPositions();
        }, 100);

        return () => {
            if (preloadTimeoutRef.current) {
                clearTimeout(preloadTimeoutRef.current);
            }
        };
    }, [preloadAnchorPositions]);

    useEffect(() => {
        const handleResize = () => {
            globalPositionCache.clear();
            
            if (preloadTimeoutRef.current) {
                clearTimeout(preloadTimeoutRef.current);
            }
            
            preloadTimeoutRef.current = setTimeout(() => {
                preloadAnchorPositions();
            }, 200);
        };

        window.addEventListener('resize', handleResize, { passive: true });
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [preloadAnchorPositions]);

    return {
        scrollToElement,
        scrollToHref,
        preloadAnchorPositions,
        isScrolling: isScrollingGlobal
    };
};



export default useInstantScroll;